import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  Typography, Box, CircularProgress, Chip, Alert, IconButton,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { createFlow } from '../api/whatsapp';
import client from '../api/client';

const BuildWithAIModal = ({ open, onClose, onCreated }) => {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleGenerate = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await client.post('/api/whatsapp/flows/generate', { description: description.trim() });
      setResult(res.data);
    } catch {
      setError('AI generation failed. Please try again or create manually.');
    }
    setLoading(false);
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (!kw || result.keywords.includes(kw)) return;
    setResult(prev => ({ ...prev, keywords: [...prev.keywords, kw] }));
    setKeywordInput('');
  };

  const removeKeyword = (kw) => {
    setResult(prev => ({ ...prev, keywords: prev.keywords.filter(k => k !== kw) }));
  };

  const handleSave = async () => {
    if (!result?.keywords?.length && !result?.greetingMessage) return;
    setSaving(true);
    try {
      await createFlow({
        name: description.trim().slice(0, 60) || 'AI Generated Flow',
        triggerKeywords: result.keywords || [],
        matchType: 'contains',
        greetingMessage: result.greetingMessage || '',
        status: 'active',
      });
      onCreated();
    } catch {
      setError('Failed to save flow');
    }
    setSaving(false);
  };

  const handleClose = () => {
    setDescription('');
    setResult(null);
    setError('');
    setKeywordInput('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <AutoAwesomeIcon sx={{ color: '#f59e0b' }} />
        <Typography fontWeight={700} fontSize={16}>Build with AI</Typography>
        <IconButton onClick={handleClose} sx={{ ml: 'auto', color: '#94a3b8' }} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {!result ? (
          <>
            <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
              Describe your business and what you offer. AI will generate trigger keywords and a greeting message for your WhatsApp chatbot flow.
            </Typography>
            <TextField
              autoFocus multiline rows={4} fullWidth size="small"
              placeholder="e.g. I run a boutique hotel in Goa, offering luxury rooms, spa services, and restaurant reservations."
              value={description} onChange={e => setDescription(e.target.value)}
              sx={{ '& textarea': { fontSize: 13 } }}
            />
            {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
          </>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>
              AI generated the following flow. You can edit before saving.
            </Alert>

            <Typography fontWeight={600} fontSize={13} color="#1e293b" sx={{ mb: 1 }}>Trigger Keywords</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              {result.keywords?.map((kw, i) => (
                <Chip key={i} label={kw} onDelete={() => removeKeyword(kw)} size="small"
                  sx={{ bgcolor: '#f0f2f5', color: '#1e293b', fontWeight: 500 }} />
              ))}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <TextField size="small" placeholder="Add keyword" value={keywordInput}
                  onChange={e => setKeywordInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                  sx={{ '& input': { fontSize: 12, py: 0.5 }, width: 120 }}
                />
                <IconButton size="small" onClick={addKeyword} sx={{ color: '#075e54' }}><AddIcon fontSize="small" /></IconButton>
              </Box>
            </Box>

            <Typography fontWeight={600} fontSize={13} color="#1e293b" sx={{ mb: 1 }}>Greeting / Auto-Reply Message</Typography>
            <TextField multiline rows={3} fullWidth size="small" value={result.greetingMessage || ''}
              onChange={e => setResult(prev => ({ ...prev, greetingMessage: e.target.value }))}
              sx={{ '& textarea': { fontSize: 13 } }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button onClick={handleClose} sx={{ color: '#64748b', textTransform: 'none', borderRadius: 2 }}>
          Cancel
        </Button>
        {!result ? (
          <Button variant="contained" onClick={handleGenerate} disabled={loading || !description.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#075e54', '&:hover': { bgcolor: '#054d44' } }}>
            {saving ? 'Saving...' : 'Save Flow'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BuildWithAIModal;
